import { Link } from "react-router-dom";
import { MapPin, Calendar, Clock, Users, Navigation } from "lucide-react";
import { VerifiedBadge } from "./VerifiedBadge";
import { formatDate, formatTime } from "../lib/dateUtils";

const TYPE_LABEL = { romantic: "Romantic", friendship: "Friendship", creative: "Creative" };

export const SessionCard = ({ session }) => {
  const fullyBooked = session.spots_remaining <= 0;
  const spotsLabel = fullyBooked ? "Fully booked" : session.spots_remaining === 1 ? "1 spot left" : `${session.spots_remaining} spots left`;
  return (
    <Link
      to={`/sessions/${session.id}`}
      data-testid={`session-card-${session.id}`}
      className="group block bg-clean-white border border-soft-gray rounded-2xl overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-stranger-orange"
    >
      <div className="relative h-56 overflow-hidden">
        <img src={session.image_url} alt={session.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
        <span className="absolute top-4 left-4 bg-midnight-black/80 text-clean-white text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full">
          {TYPE_LABEL[session.session_type] || session.session_type}
        </span>
        {session.photographer_verified && (
          <div className="absolute top-4 right-4">
            <VerifiedBadge compact />
          </div>
        )}
        {fullyBooked && (
          <span className="absolute bottom-4 left-4 bg-red-600 text-clean-white text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full" data-testid={`fully-booked-badge-${session.id}`}>
            Fully Booked
          </span>
        )}
      </div>
      <div className="p-6">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="text-lg font-extrabold uppercase tracking-tight leading-snug">{session.title}</h3>
          {session.distance_miles != null && (
            <span className="flex items-center gap-1 text-xs font-bold text-stranger-orange whitespace-nowrap shrink-0 mt-1" data-testid={`session-distance-${session.id}`}>
              <Navigation className="w-3 h-3" /> {session.distance_miles} mi
            </span>
          )}
        </div>
        <p className="text-sm text-midnight-black/60 mb-3 font-medium">Hosted by {session.photographer_name}</p>
        <div className="space-y-1.5 text-sm font-medium text-midnight-black/80 mb-4">
          <p className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-stranger-orange" /> {session.city}, {session.state}
          </p>
          <p className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-stranger-orange" /> {formatDate(session.session_date)}
          </p>
          <p className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-stranger-orange" /> {formatTime(session.session_time)}
          </p>
          <p className={`flex items-center gap-2 ${fullyBooked ? "text-red-600 font-bold" : ""}`}>
            <Users className="w-4 h-4 text-stranger-orange" /> {spotsLabel}
          </p>
        </div>
        <div className="flex items-center justify-between pt-4 border-t border-soft-gray">
          <span className="text-xl font-extrabold">${session.price}</span>
          <span data-testid={`view-session-${session.id}`} className="text-xs font-bold tracking-widest bg-midnight-black text-clean-white px-5 py-3 rounded-full group-hover:bg-stranger-orange transition-colors">
            VIEW SESSION
          </span>
        </div>
      </div>
    </Link>
  );
};
